import Modal from '@atlaskit/modal-dialog';
import React, { useState } from 'react';
import { sendRecognitionModalOpenClickEvent } from 'analytics/kudosAnalytics';
import { css } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import KudosModalBody from './KudosModalBody';
import { useKudosModalData } from 'hooks/useKudosModalData';
import { useGiveRecognition } from 'hooks/useGiveRecognition';
import { KudosModalFooter } from './KudosModalFooter';
import { KudosModalHeader } from './KudosModalHeader';
import { KudosModalFlags } from './KudosModalFlags';
import { LimitReachedModal } from './LimitReachedModal';

interface Props {
  currentProfileAaid: string;
}

const KudosModal = ({ currentProfileAaid }: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const [flags, setFlags] = React.useState<Array<number>>([]);
  const [isReasonSelected, setIsReasonSelected] = useState(false);
  const [selectedReason, setSelectedReason] = useState<string | undefined>();

  const { userData, recognitionSchedule, loggedInUserAaid, pathProfileId } =
    useKudosModalData({ currentProfileAaid });

  const closeModal = () => setIsOpen(false);

  const openModal = () => {
    sendRecognitionModalOpenClickEvent({
      senderAaid: loggedInUserAaid ?? '',
      receiverAaid: currentProfileAaid,
      profileInternalId: pathProfileId ?? '',
    });
    setIsOpen(true);
  };

  const { giveRecognition, isSubmitting } = useGiveRecognition({
    onSuccess: () => {
      setFlags([flags.length + 1, ...flags]);
      setIsReasonSelected(false);
      setSelectedReason(undefined);
    },
    onComplete: closeModal,
    profileInternalId: pathProfileId ?? '',
  });

  const sendRecognitionButton = () => {
    giveRecognition({
      currentProfileAaid,
      comment: selectedReason ?? '',
      loggedInUserAaid: loggedInUserAaid ?? '',
      pathProfileId: pathProfileId ?? '',
    });
  };

  const handleDismiss = () => {
    setFlags(flags.slice(1));
  };

  const userName = userData?.nickname;
  const firstName = userName ? userName.split(' ')[0] : null;

  const shouldShowLimitModal = () =>
    isOpen && recognitionSchedule?.availablePoints === 0;
  const shouldShowRegularModal = () =>
    isOpen && (recognitionSchedule?.availablePoints ?? 0) > 0;

  return (
    <div>
      <button css={cursorStyles} onClick={openModal}>
        {`Give ${firstName} recognition`}
      </button>
      {shouldShowLimitModal() && (
        <LimitReachedModal
          recognitionSchedule={recognitionSchedule}
          onClose={closeModal}
        />
      )}
      {shouldShowRegularModal() && (
        <Modal onClose={closeModal}>
          <div css={GiveRecognitionModalStyles}>
            <KudosModalHeader
              recognitionSchedule={recognitionSchedule}
              onClose={closeModal}
            />
            <KudosModalBody
              onReasonSelect={(reason) => {
                setIsReasonSelected(true);
                setSelectedReason(reason);
              }}
            />
            <KudosModalFooter
              onSendRecognition={sendRecognitionButton}
              isReasonSelected={isReasonSelected}
              isSubmitting={isSubmitting}
            />
          </div>
        </Modal>
      )}
      <KudosModalFlags
        flags={flags}
        handleDismiss={handleDismiss}
        userName={userData?.nickname ?? ''}
      />
    </div>
  );
};

const GiveRecognitionModalStyles = css({
  boxSizing: 'border-box',
  width: '100%',
  backgroundColor: 'white',
  margin: '0 auto',
});

const cursorStyles = css({
  cursor: 'pointer',
  '&:hover': {
    textDecoration: 'underline',
  },
  color: token('color.text.brand'),
  textAlign: 'center',
  fontFamily: token('font.family.brand.body'),
  fontSize: '16px',
  fontWeight: token('font.weight.semibold'),
  lineHeight: '20px',
  whiteSpace: 'nowrap',
  border: 'none',
  backgroundColor: 'white',
  padding: '0px',
});

export default KudosModal;
